import type { CSSProperties, ReactNode } from 'react';
import { percentOf, type TopGoal, type TopShare, type TopSummary } from './sheetTop';

// 시트 탭 머리글 위쪽 요약 칸을 콘솔에서(2026-10-23) — 값은 시트에 보이는 글자 그대로, 읽기만.
// 오너 "너무 난잡한데 좀 정리해야겠다" → 카드 여러 개 대신 판 두 개:
//   ① 성과 — 목표 열을 한 줄에(달성 / 목표 · 달성률 막대)
//   ② 현황 — 문의 방법 · 주차별 · 통화 결과 · 비율(담당자 · 권역)을 가는 세로선으로 나눈 한 판
// 담당자 · 주차처럼 아래 표를 거를 수 있는 칸은 눌러서 거른다(pick).

export interface SheetTopPick {
  /** 이 이름(담당자 'Dylan' · '1주차')으로 표를 거를 수 있나 */
  pickable: (label: string) => boolean;
  active: string;
  onPick: (label: string) => void;
}

export interface TopItem {
  label: string;
  value: string;
  /** 이름 앞 작은 표시(문의 방법 심볼 · 시트 칸 색 점) */
  mark?: ReactNode;
  /** 값 옆 옅은 글자('50.0%') */
  sub?: string;
  active?: boolean;
  onClick?: () => void;
  testId?: string;
}

export function SheetTopSummary({
  summary,
  pick,
  lead,
  titleOf,
  markOf,
}: {
  summary: TopSummary;
  pick?: SheetTopPick;
  /** 현황 판 맨 앞 칸(화면이 직접 세는 것 — 문의 방법) */
  lead?: { title: string; items: TopItem[] };
  /** 시트에 제목 칸이 없는 묶음의 제목(칸 이름들로) — 예: 통화 · 부재 → '연락 여부' */
  titleOf?: (labels: string[]) => string | undefined;
  /** 칸 이름 앞 표시(없으면 시트 칸 색 점) — 예: 통화 → 전화 아이콘 */
  markOf?: (label: string, color?: string) => ReactNode;
}) {
  const nameOf = (title: string | undefined, labels: string[]) => title ?? titleOf?.(labels);
  // 모양을 알아보지 못한 칸(summary.rest)은 보이지 않는다 — 26.08 탭 AF2:AI3 의 이름 없는 계산이
  // '15 · 창업 4 40% · 업종변경 2' 로 떠서 오너가 "요약에서 빼"(2026-10-23).
  const { goals, stats, shares } = summary;
  const toItem = (label: string, value: string, bg?: string, sub?: string): TopItem => {
    const can = !!pick?.pickable(label);
    return {
      label,
      value,
      sub,
      mark:
        markOf?.(label, bg) ??
        (bg ? <span style={{ ...dot, background: bg }} aria-hidden /> : undefined),
      active: can && pick!.active === label,
      onClick: can ? () => pick!.onPick(label) : undefined,
    };
  };
  const hasBoard = !!lead || stats.length > 0 || shares.length > 0;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="sheet-top">
      {goals.length > 0 && (
        <div style={{ ...panel, ...goalGrid }} data-testid="sheet-top-goals">
          {goals.map((g) => (
            <Goal key={g.label} goal={g} />
          ))}
        </div>
      )}
      {hasBoard && (
        <div style={{ ...panel, ...board }}>
          {[
            ...(lead ? [{ title: lead.title, items: lead.items }] : []),
            ...stats.map((g) => ({
              title: nameOf(
                g.title,
                g.items.map((it) => it.label),
              ),
              items: g.items.map((it) => toItem(it.label, it.value, it.bg)),
            })),
            ...shares.map((g) => ({
              title: nameOf(
                undefined,
                g.map((it) => it.label),
              ),
              bar: <ShareBar items={g} />,
              items: g.map((it, j) => {
                const { main, pct } = splitShare(it.value);
                return toItem(it.label, main, shareColor(it, j), pct);
              }),
            })),
          ].map((sec, i) => (
            <Section key={i} divided={i > 0} {...sec} />
          ))}
        </div>
      )}
    </section>
  );
}

function Goal({ goal }: { goal: TopGoal }) {
  const pct = percentOf(goal.rate);
  const hasBar = goal.done !== undefined || goal.target !== undefined;
  // 달성 값이 없는 목표 열은 '–'(시트에서 비어 있다), 비율만 있는 열('데모전환율')은 그 비율
  const main = goal.done ?? (goal.target !== undefined ? '–' : (goal.rate ?? ''));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span className="kuun-caption" style={muted}>
        {goal.label}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, whiteSpace: 'nowrap' }}>
        <strong style={{ fontSize: 17 }}>{main}</strong>
        {goal.target !== undefined && (
          <span className="kuun-caption" style={muted}>
            / {goal.target}
          </span>
        )}
        {hasBar && goal.rate && (
          <span className="kuun-caption" style={{ ...muted, marginLeft: 'auto' }}>
            {goal.rate}
          </span>
        )}
      </div>
      {hasBar && (
        <div style={track} aria-hidden>
          <div
            style={{
              height: '100%',
              borderRadius: 999,
              width: `${Math.max(0, Math.min(100, pct ?? 0))}%`,
              background: 'var(--brand)',
            }}
          />
        </div>
      )}
      {goal.extra.map(([k, v]) => (
        <span key={k} className="kuun-caption" style={muted}>
          {k} {v}
        </span>
      ))}
    </div>
  );
}

function Section({
  title,
  items,
  bar,
  divided,
}: {
  title?: string;
  items: TopItem[];
  bar?: ReactNode;
  divided?: boolean;
}) {
  return (
    <div style={divided ? { ...section, borderLeft: '1px solid var(--border-subtle)' } : section}>
      {/* 제목 줄은 늘 있다(없으면 빈 줄) — 칸마다 제목 · 숫자 높이가 같게(2026-10-23 오너 "높낮이 다른거") */}
      <span className="kuun-caption" style={{ ...muted, fontWeight: 600, padding: '0 6px' }}>
        {title ?? '\u00a0'}
      </span>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <Item key={`${it.label}${i}`} item={it} apart={i > 0 && /^전체/.test(it.label)} />
        ))}
      </div>
      {bar}
    </div>
  );
}

/** apart: 묶음 끝의 합계('전체 인입')를 앞 칸들과 조금 띄운다(2026-10-23 오너 "전체인입만 살짝 띄우는거야") */
function Item({ item, apart }: { item: TopItem; apart?: boolean }) {
  const body = (
    <>
      {item.label && (
        <span
          className="kuun-caption"
          style={{ ...muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {item.mark}
          {item.label}
        </span>
      )}
      <span
        style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, whiteSpace: 'nowrap' }}
      >
        <strong style={{ fontSize: 15 }} data-testid={item.testId}>
          {item.value}
        </strong>
        {item.sub && (
          <span className="kuun-caption" style={muted}>
            {item.sub}
          </span>
        )}
      </span>
    </>
  );
  const gap = apart ? { marginLeft: 12 } : null;
  if (!item.onClick) return <div style={{ ...cell, ...gap }}>{body}</div>;
  return (
    <button
      type="button"
      className="kuun-sheet-kpi"
      onClick={item.onClick}
      aria-pressed={!!item.active}
      style={{ ...cell, ...cellBtn, ...gap, ...(item.active ? cellOn : null) }}
    >
      {body}
    </button>
  );
}

/** 비율 묶음 위의 얇은 막대 — 칸마다 시트 색(없으면 회색 단계) */
function ShareBar({ items }: { items: TopShare[] }) {
  const parts = items.map((it) => ({ it, pct: percentOf(splitShare(it.value).pct) ?? 0 }));
  const sum = parts.reduce((a, p) => a + p.pct, 0);
  if (!sum) return null;
  return (
    <div style={{ ...track, display: 'flex', height: 5, margin: '2px 6px' }} aria-hidden>
      {parts.map(({ it, pct }, i) => (
        <div
          key={it.label}
          style={{
            width: `${(pct / sum) * 100}%`,
            background: shareColor(it, i),
          }}
        />
      ))}
    </div>
  );
}

/**
 * 비율 칸 색 — 시트 칸 색(담당자)이 없으면 차례대로 서로 대비되는 색(권역: 서울+경기권 주황 · 나머지권역 청록 — 오너가 자리 바꿈).
 * 2026-10-23 오너 "이거 구분하는거도 색상화" → "빨강 파랑 하니까 쫌 그렇다" → "둘이 대비되는 색이긴 해야해".
 */
const SHARE_PALETTE = ['#e9a23b', '#2a9d8f', '#8e7cc3', '#7f8c8d'];
function shareColor(it: TopShare, i: number): string {
  return it.bg ?? SHARE_PALETTE[i % SHARE_PALETTE.length]!;
}

/** '48건 (41.0%)' → { main: '48건', pct: '41.0%' } */
function splitShare(v: string): { main: string; pct?: string } {
  const m = /^(.*?)\s*\(([^()]*%)\)\s*$/.exec(v);
  return m ? { main: m[1]!, pct: m[2]! } : { main: v };
}

const muted: CSSProperties = { color: 'var(--fg-muted)' };
const panel: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--r-card)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};
const goalGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
  gap: '10px 24px',
};
const board: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  rowGap: 10,
  padding: '8px 6px',
};
const section: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 2,
  padding: '0 10px',
};
// 이름 · 숫자는 가운데(2026-10-23 오너 "글자 가운데 정렬해야지")
const cell: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0,
  minWidth: 44,
  padding: '3px 6px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--fg-default)',
  textAlign: 'center',
};
const cellBtn: CSSProperties = { cursor: 'pointer' };
const cellOn: CSSProperties = {
  border: '1px solid color-mix(in srgb, var(--brand) 45%, var(--border-default))',
  background: 'color-mix(in srgb, var(--brand) 8%, var(--bg-surface))',
};
const dot: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 999,
  display: 'inline-block',
  flexShrink: 0,
};
const track: CSSProperties = {
  height: 4,
  borderRadius: 999,
  background: 'color-mix(in srgb, var(--fg-default) 10%, transparent)',
  overflow: 'hidden',
};
